import React, { useEffect, useState } from "react";
import { useAuth } from "../auth";
import { useSiteSettings } from "../siteSettings";
import PasswordField from "./PasswordField";
import { friendlyError } from "../utils/errors";

function GoogleIcon() {
  return (
    <svg
      className="reg-google-icon"
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      <path
        fill="#EA4335"
        d="M12 10.2v3.6h5.1c-.2 1.2-.9 2.2-1.9 2.9l3.1 2.4c1.8-1.7 2.9-4.1 2.9-7 0-.7-.1-1.4-.2-2H12z"
      />
      <path
        fill="#34A853"
        d="M6.6 14.3l-.8.6-2.7 2.1C4.8 20.1 8.1 22 12 22c2.7 0 5-.9 6.7-2.4l-3.1-2.4c-.9.6-2 .9-3.6.9-2.8 0-5.1-1.9-5.9-4.4z"
      />
      <path
        fill="#4A90E2"
        d="M3.1 7.1C2.4 8.4 2 9.9 2 11.5s.4 3.1 1.1 4.4l3.5-2.7c-.2-.6-.3-1.2-.3-1.7 0-.6.1-1.2.3-1.7L3.1 7.1z"
      />
      <path
        fill="#FBBC05"
        d="M12 5.9c1.5 0 2.8.5 3.8 1.5l2.8-2.8C16.9 2.9 14.7 2 12 2 8.1 2 4.8 3.9 3.1 7.1l3.5 2.7C7 7.8 9.2 5.9 12 5.9z"
      />
    </svg>
  );
}

const COPY = {
  signin: {
    title: "Entra a tu cuenta",
    desc: "Administra tus equipos, revisa tus solicitudes y registra equipos nuevos.",
    submit: "Entrar",
  },
  signup: {
    title: "Crea tu cuenta",
    desc: "Es gratis. La necesitas para registrar un equipo en la liga.",
    submit: "Crear cuenta",
  },
  reset: {
    title: "Recupera tu contraseña",
    desc: "Te enviamos un enlace para crear una contraseña nueva.",
    submit: "Enviar enlace",
  },
};

const AuthPanel = () => {
  const {
    signIn,
    signUp,
    signInWithGoogle,
    requestPasswordReset,
    authError,
    clearAuthError,
  } = useAuth();
  // Per-client setting; hidden until settings load, and whenever it is off.
  const { loaded: settingsLoaded, googleAuthEnabled } = useSiteSettings();
  const showGoogle = settingsLoaded && googleAuthEnabled;
  const [mode, setMode] = useState("signin"); // signin | signup | reset
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (authError) {
      setError(authError);
      clearAuthError();
    }
  }, [authError, clearAuthError]);

  const switchMode = (next) => {
    setMode(next);
    setError(null);
    setInfo(null);
    setPassword("");
    setConfirm("");
  };

  const onGoogle = async () => {
    setError(null);
    setInfo(null);
    setBusy(true);
    try {
      await signInWithGoogle();
      // Browser redirects to Google; no further UI update here.
    } catch (err) {
      console.error(err);
      setBusy(false);
      const msg = err?.message || "";
      if (/provider is not enabled|unsupported provider/i.test(msg)) {
        setError(
          "El inicio con Google no está disponible por ahora. Usa correo y contraseña."
        );
      } else {
        setError(friendlyError(err, "No se pudo iniciar con Google. Inténtalo de nuevo."));
      }
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setInfo(null);

    if (mode === "reset") {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
        setError("Escribe el correo de tu cuenta.");
        return;
      }
      setBusy(true);
      try {
        await requestPasswordReset(email);
        setInfo(
          `Si ${email.trim()} tiene una cuenta, te llegará un enlace en unos minutos. Ábrelo en este mismo navegador.`
        );
      } catch (err) {
        console.error(err);
        const msg = err?.message || "";
        setError(
          /rate limit|too many/i.test(msg)
            ? "Ya enviamos un enlace hace poco. Espera un minuto e inténtalo de nuevo."
            : friendlyError(err, "No se pudo enviar el enlace. Inténtalo de nuevo.")
        );
      } finally {
        setBusy(false);
      }
      return;
    }

    if (!email.trim() || !password) {
      setError("Correo y contraseña son obligatorios.");
      return;
    }
    if (password.length < 6) {
      setError("La contraseña debe tener al menos 6 caracteres.");
      return;
    }
    if (mode === "signup") {
      if (!fullName.trim()) {
        setError("Tu nombre es obligatorio.");
        return;
      }
      const digits = phone.replace(/\D/g, "");
      if (!phone.trim() || digits.length < 7) {
        setError("Ingresa un teléfono válido.");
        return;
      }
      if (password !== confirm) {
        setError("Las contraseñas no coinciden.");
        return;
      }
    }

    setBusy(true);
    try {
      if (mode === "signin") {
        await signIn(email, password);
      } else {
        const data = await signUp(email, password, {
          fullName: fullName.trim(),
          phone: phone.trim(),
        });
        if (data.session) {
          setInfo(null);
        } else {
          setMode("signin");
          setPassword("");
          setConfirm("");
          setInfo(
            "Revisa tu correo para confirmar la cuenta, luego inicia sesión."
          );
        }
      }
    } catch (err) {
      console.error(err);
      const msg = err?.message || "";
      if (/invalid login|invalid credentials/i.test(msg)) {
        setError("Correo o contraseña incorrectos.");
      } else if (/already registered|already been registered/i.test(msg)) {
        setError("Ese correo ya tiene cuenta. Inicia sesión.");
        setMode("signin");
      } else if (/email.*confirm|confirm.*email/i.test(msg)) {
        setError("Confirma tu correo antes de iniciar sesión.");
      } else {
        setError(friendlyError(err, "No se pudo completar. Inténtalo de nuevo."));
      }
    } finally {
      setBusy(false);
    }
  };

  const copy = COPY[mode];

  return (
    <div className="reg-auth acct-auth">
      <header className="acct-auth-head">
        <h2 className="acct-auth-title">{copy.title}</h2>
        <p className="acct-auth-desc">{copy.desc}</p>
      </header>

      {mode !== "reset" && showGoogle && (
        <>
          <button
            type="button"
            className="reg-google"
            onClick={onGoogle}
            disabled={busy}
          >
            <GoogleIcon />
            Continuar con Google
          </button>

          <div className="reg-auth-divider" role="separator">
            <span>o con tu correo</span>
          </div>
        </>
      )}

      <form className="reg-form" onSubmit={onSubmit} noValidate>
        {mode === "signup" && (
          <>
            <label className="reg-field">
              <span className="reg-label">
                Nombre completo <span className="reg-req">*</span>
              </span>
              <input
                type="text"
                name="name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                autoComplete="name"
                disabled={busy}
                maxLength={80}
                required
              />
            </label>

            <label className="reg-field">
              <span className="reg-label">
                Teléfono <span className="reg-req">*</span>
              </span>
              <input
                type="tel"
                name="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                autoComplete="tel"
                inputMode="tel"
                placeholder="915-555-0142"
                disabled={busy}
                maxLength={30}
                required
              />
            </label>
          </>
        )}

        <label className="reg-field">
          <span className="reg-label">
            Correo <span className="reg-req">*</span>
          </span>
          <input
            type="email"
            name="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            inputMode="email"
            spellCheck={false}
            disabled={busy}
            required
          />
        </label>

        {mode !== "reset" && (
          <PasswordField
            label="Contraseña"
            value={password}
            onChange={setPassword}
            autoComplete={mode === "signin" ? "current-password" : "new-password"}
            disabled={busy}
            hint={mode === "signup" ? "Mínimo 6 caracteres." : undefined}
          />
        )}

        {mode === "signin" && (
          <button
            type="button"
            className="acct-link acct-forgot"
            onClick={() => switchMode("reset")}
            disabled={busy}
          >
            ¿Olvidaste tu contraseña?
          </button>
        )}

        {mode === "signup" && (
          <PasswordField
            label="Confirmar contraseña"
            value={confirm}
            onChange={setConfirm}
            autoComplete="new-password"
            disabled={busy}
          />
        )}

        <div aria-live="polite">
          {error && (
            <div className="reg-status error" role="alert">
              {error}
            </div>
          )}
          {info && (
            <div className="reg-success" role="status">
              {info}
            </div>
          )}
        </div>

        <button type="submit" className="reg-submit" disabled={busy}>
          {busy ? "Un momento…" : copy.submit}
        </button>
      </form>

      <p className="acct-auth-switch">
        {mode === "signin" && (
          <>
            ¿No tienes cuenta?{" "}
            <button type="button" className="acct-link" onClick={() => switchMode("signup")} disabled={busy}>
              Crear cuenta
            </button>
          </>
        )}
        {mode === "signup" && (
          <>
            ¿Ya tienes cuenta?{" "}
            <button type="button" className="acct-link" onClick={() => switchMode("signin")} disabled={busy}>
              Inicia sesión
            </button>
          </>
        )}
        {mode === "reset" && (
          <button type="button" className="acct-link" onClick={() => switchMode("signin")} disabled={busy}>
            Volver a iniciar sesión
          </button>
        )}
      </p>
    </div>
  );
};

export default AuthPanel;
